import { clsx, type ClassValue } from 'clsx';
import { debounce } from 'lodash-es';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatTimeAgo(timestamp: number | undefined): string {
  if (!timestamp) {
    return '';
  }
  const now = Date.now();
  const diffInSeconds = Math.floor((now - timestamp) / 1000);
  const minutes = Math.floor(diffInSeconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  switch (true) {
    case diffInSeconds < 300:
      return 'recently';
    case diffInSeconds < 3600:
      return `${String(minutes)} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
    case diffInSeconds < 86400:
      return `${String(hours)}${hours === 1 ? 'hr' : 'hrs'} ago`;
    case diffInSeconds < 2592000:
      return `${String(days)}d ago`;
    case diffInSeconds < 31536000:
      return `${String(months)}mo ago`;
    default:
      return `${String(years)}${years === 1 ? 'yr' : 'yrs'} ago`;
  }
}

export const debounceFunc = <T>(func: (...args: T[]) => void, delay = 300) => {
  return debounce((...args: T[]) => {
    func(...args);
  }, delay);
};

/**
 * Get normalized timestamp in milliseconds (rounded to seconds precision)
 * Used for consistent timestamp handling across database operations
 */
export function now(): number {
  return ((Date.now() / 1000) | 0) * 1000;
}

/**
 * Get the current subdomain from the hostname
 * @returns The subdomain string or null if no subdomain
 */
export function getSubdomain(): string | null {
  if (typeof window === 'undefined') return null;

  const hostname = window.location.hostname;
  const parts = hostname.split('.');

  // For localhost development - check for query parameter to simulate subdomain
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('subdomain');
  }

  // If we have at least 3 parts (subdomain.domain.tld), return the first part
  if (parts.length >= 3) {
    return parts[0];
  }

  return null;
}

/**
 * Check if the current domain is the valid public subdomain
 * @param validSubdomain The subdomain that should serve public content
 * @returns True if we're on the valid public subdomain
 */
export function isValidPublicSubdomain(validSubdomain = 'denis'): boolean {
  const subdomain = getSubdomain();
  return subdomain === validSubdomain;
}

/**
 * Check if we're on the main domain (no subdomain or invalid subdomain)
 * @returns True if we're on the main domain
 */
export function isMainDomain(): boolean {
  return !isValidPublicSubdomain();
}
